import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { deleteProject } from '../../services/api';
import ProjectsTable from './ProjectsTable';
import { useModal } from '../../contexts/ModalContext';
import CustomSelect from '../ui/CustomSelect';

const PROMPT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'System User', label: 'System + User' },
  { value: 'User Only', label: 'User Only' },
  { value: 'few-shot', label: 'Few-Shot Examples' },
];

const ProjectsList = ({ projects, selectedProjectId, onSelectProject, onDelete, loading, onNewProject }) => {
  const { confirmDelete } = useModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPromptType, setSelectedPromptType] = useState('all');

  // Filter projects based on search query and prompt type
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Filter by prompt type
      const projectType = project.user_input?.type || project.prompt_type || '';
      let typeMatch = selectedPromptType === 'all';
      
      if (!typeMatch) {
        if (selectedPromptType === 'few-shot') {
          // Match few-shot variants (handles spacing variations)
          typeMatch = projectType.toLowerCase().includes('assistant') || 
                      projectType.toLowerCase().includes('few') ||
                      projectType.includes('System  User  Assistant');
        } else {
          typeMatch = projectType === selectedPromptType;
        }
      }
      
      // Filter by search query (search in name and description)
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = project.project_name?.toLowerCase().includes(searchLower);
      const descMatch = project.user_input?.description?.toLowerCase().includes(searchLower);
      const searchMatch = !searchQuery || nameMatch || descMatch;

      return typeMatch && searchMatch;
    });
  }, [projects, selectedPromptType, searchQuery]);

  const hasActiveFilters = selectedPromptType !== 'all' || searchQuery;
  
  const handleDelete = async (e, project) => {
    e.stopPropagation(); // Prevent card click
    
    const confirmed = await confirmDelete(project.project_name);
    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteProject(project.id);
      if (result.success) {
        toast.success('Prompt deleted successfully!');
        if (onDelete) {
          onDelete(project.id);
        }
      } else {
        toast.error(result.error || 'Failed to delete prompt');
      }
    } catch (error) {
      toast.error('Failed to delete prompt');
    }
  };
  
  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10">
        <div className="doc-loading-spinner"></div>
        <p className="text-text-secondary text-sm">Loading documents...</p>
      </div>
    );
  }

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPromptType('all');
  };

  return (
    <div className="h-full flex flex-col bg-transparent overflow-hidden">
      {/* Document Header */}
      <div className="doc-page-header">
        <div className="doc-header-left">
          <div className="doc-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
            </svg>
          </div>
          <div className="doc-header-content">
            <h1 className="doc-header-title">Prompt Library</h1>
            <p className="doc-header-subtitle">
              <span className="doc-count">{filteredProjects.length}</span>
              {filteredProjects.length === 1 ? ' prompt' : ' prompts'}
              {hasActiveFilters && projects.length !== filteredProjects.length && (
                <span className="text-text-tertiary"> of {projects.length}</span>
              )}
            </p>
          </div>
        </div>
        
        <div className="doc-header-actions">
          <button onClick={onNewProject} className="doc-new-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14m-7-7h14"/>
            </svg>
            <span className="hidden sm:inline">New Prompt</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="px-4 py-3 border-b border-border-primary/50 flex flex-col sm:flex-row gap-3 sm:items-center">
        {/* Search Input */}
        <div className="relative w-full sm:w-auto sm:min-w-[300px] sm:max-w-[480px]">
          <svg 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        {/* Prompt Type Filter */}
        <div className="relative flex items-center gap-2">
          <div className="flex items-center gap-2 text-text-tertiary">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            <span className="text-xs font-medium hidden sm:inline">Filter:</span>
          </div>
          <CustomSelect
            value={selectedPromptType}
            onChange={(e) => setSelectedPromptType(e.target.value)}
            options={PROMPT_TYPE_OPTIONS}
            placeholder="All Types"
            size="small"
          />
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-secondary border border-border-primary rounded-lg transition-all flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Clear
          </button>
        )}
      </div>
      
      {/* Document List */}
      <ProjectsTable
        projects={filteredProjects}
        selectedProjectId={selectedProjectId}
        onSelectProject={onSelectProject}
        onDelete={handleDelete}
        emptyStateConfig={{
          title: hasActiveFilters ? 'No Matching Prompts' : 'No Prompts Yet',
          description: hasActiveFilters 
            ? 'No prompts match your current filters. Try adjusting your search or filter criteria.'
            : 'Start building your AI prompts by creating your first document. Organize and manage your prompt templates efficiently.',
          actionLabel: hasActiveFilters ? 'Clear Filters' : 'Create Your First Prompt',
          onAction: hasActiveFilters ? clearFilters : onNewProject
        }}
      />
    </div>
  );
};

ProjectsList.propTypes = {
  projects: PropTypes.array.isRequired,
  selectedProjectId: PropTypes.string,
  onSelectProject: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  loading: PropTypes.bool,
  onNewProject: PropTypes.func,
};

ProjectsList.defaultProps = {
  selectedProjectId: null,
  onDelete: null,
  loading: false,
};

export default ProjectsList;
